import { makeAutoObservable, runInAction } from "mobx";

import type { IBrandGetById } from "@/api/admin/brands";
import { getCuisineIdsFromBrandProperties } from "@/api/admin/brands/brandPropertyUtils";
import type {
  BrandAmenitiesType,
  BrandContactsType,
  BrandFormDetailsType,
  BrandFormMenuType,
  BrandVibesType,
} from "@/components/units";
import { mapMenuGetToForm } from "@/components/units/BrandFormMenu/menuMappers";
import { DEFAULT_NAME } from "@/utils/constants/defaultNames";

import type { Branch } from "./BranchStore";
import { rootStore } from "./RootStore";

export interface BrandFormPayload {
  details?: BrandFormDetailsType;
  menu?: BrandFormMenuType;
  contacts?: BrandContactsType;
  vibes?: BrandVibesType;
  amenities?: BrandAmenitiesType;
  branches?: Branch[];
  active?: boolean;
}

export type BrandFullPayload = BrandFormPayload & {
  branches: Branch[];
  active?: boolean;
};

export class BrandStore {
  details: BrandFormDetailsType | null = null;
  menu: BrandFormMenuType | null = null;
  contacts: BrandContactsType | null = null;
  vibes: BrandVibesType | null = null;
  amenities: BrandAmenitiesType | null = null;
  branches: Branch[] = [];
  brand: BrandFormPayload | null = null;
  active: boolean = true;

  constructor() {
    makeAutoObservable(this);
    this.initDefaults();
  }

  // TODO: after full integration - check. Maybe this method should be deleted
  init(brand: BrandFullPayload) {
    runInAction(() => {
      const { branches, ...brandPayload } = brand;
      this.brand = brandPayload;
      this.branches = branches;

      this.details = brandPayload?.details ?? null;
      this.menu = brandPayload?.menu ?? null;
      this.contacts = brandPayload.contacts ?? null;
      this.vibes = brandPayload?.vibes ?? null;
      this.amenities = brandPayload?.amenities ?? null;

      rootStore.menuStore.initMenu(brandPayload?.menu ?? this.defaultMenu);
    });
  }

  initFromPayload(brand: IBrandGetById) {
    runInAction(() => {
      const {
        branches,
        menu,
        VAT,
        commercialRegistration,
        country,
        currency,
        descriptionAr,
        descriptionEn,
        googleBusinessId,
        groupId,
        legalEntityId,
        id,
        logoUrl,
        nameAr,
        nameEn,
        referenceNumber,
        email,
        phone,
        socialLinks,
        websiteUrl,
      } = brand;

      this.details = {
        VAT,
        commercialRegistration,
        country,
        currency,
        descriptionAr,
        descriptionEn,
        googleBusinessId,
        groupId,
        id,
        logoUrl,
        nameAr,
        nameEn,
        referenceNumber,
        legalEntityId,
      };

      const cuisineIds = getCuisineIdsFromBrandProperties(brand.properties);
      const mappedMenu = menu ? mapMenuGetToForm(menu) : null;

      this.menu = mappedMenu ? { ...mappedMenu, cuisine: cuisineIds } : null;
      this.branches = branches ?? [];

      this.contacts = {
        email,
        phone,
        socialLinks,
        websiteUrl,
      };

      this.brand = brand;
      rootStore.menuStore.initMenu(
        mappedMenu
          ? { ...mappedMenu, cuisine: cuisineIds }
          : { ...this.defaultMenu, cuisine: cuisineIds }
      );
    });
  }

  initDefaults() {
    this.details = this.defaultDetails;
    this.menu = this.defaultMenu;
    this.contacts = this.defaultContacts;
    this.vibes = this.defaultVibes;
    this.amenities = this.defaultAmenities;
  }

  setDefaultGroup(groupId: string) {
    runInAction(() => {
      this.details = {
        ...this.defaultDetails,
        groupId,
      };
    });
  }

  setActive(value: boolean) {
    if (value === false) {
      this.active = false;
    }
  }

  setAll(data: BrandFormPayload) {
    this.details = {
      ...(this.details ?? this.defaultDetails),
      ...(data.details ?? {}),
    };

    this.menu = {
      ...(this.menu ?? this.defaultMenu),
      ...(data.menu ?? {}),
    };

    this.contacts = {
      ...(this.contacts ?? this.defaultContacts),
      ...(data.contacts ?? {}),
    };

    this.vibes = {
      ...(this.vibes ?? this.defaultVibes),
      ...(data.vibes ?? {}),
    };

    this.amenities = {
      ...(this.amenities ?? this.defaultAmenities),
      ...(data.amenities ?? {}),
    };

    this.branches = data.branches ?? this.branches;

    return this.getFullPayload();
  }

  setBranches(branches: Branch[]) {
    this.branches = branches;
  }

  addBranch(branch: Branch) {
    const index = this.branches.findIndex(
      b => b.details.referenceNumber === branch.details.referenceNumber
    );
    if (index > -1) {
      this.branches[index] = branch;
    } else {
      this.branches.push(branch);
    }
  }

  getBrandName(): string {
    return this.brand?.details?.nameEn || DEFAULT_NAME;
  }

  get defaultDetails(): BrandFormDetailsType {
    return {
      googleBusinessId: "",
      descriptionEn: "",
      descriptionAr: "",
      country: "",
      currency: "",
      nameEn: "",
      nameAr: "",
      groupId: "",
      legalEntityId: "",
      logotype: [],
    };
  }

  get defaultMenu(): BrandFormMenuType {
    return {
      cuisine: [],
      categories: [],
      isActive: false,
    };
  }

  get defaultContacts(): BrandContactsType {
    return {
      email: null,
      websiteUrl: null,
      socialLinks: [{ type: "", url: "" }],
      connectedAccounts: [],
    };
  }

  get defaultVibes(): BrandVibesType {
    return {
      tags: [],
      ambiance: [],
      dressCode: [],
      priceLevel: ["1"],
    };
  }

  get defaultAmenities(): BrandAmenitiesType {
    return {
      amenities: [],
      parking: [],
    };
  }

  getFullPayload(): BrandFullPayload {
    return {
      details: this.details ?? this.defaultDetails,
      menu: this.menu ?? this.defaultMenu,
      contacts: this.contacts ?? this.defaultContacts,
      vibes: this.vibes ?? this.defaultVibes,
      amenities: this.amenities ?? this.defaultAmenities,
      branches: this.branches,
      active: this.active,
    };
  }

  prepareData() {
    const storePayload = this.getFullPayload();

    const payload = {
      ...storePayload.details,
      websiteUrl: storePayload.contacts?.websiteUrl?.trim() || null,
      email: storePayload.contacts?.email?.trim() || null,
      active: storePayload.active,
      socialLinks: storePayload.contacts?.socialLinks,
    };

    return payload;
  }

  updateGroupInfo(data: {
    country: string;
    currency: string;
    groupId: string;
  }) {
    runInAction(() => {
      this.details = {
        ...(this.details ?? this.defaultDetails),
        ...data,
      };
    });
  }

  clearStore() {
    this.details = this.defaultDetails;
    this.menu = this.defaultMenu;
    this.contacts = this.defaultContacts;
    this.vibes = this.defaultVibes;
    this.amenities = this.defaultAmenities;
    this.brand = null;
    this.branches = [];
    this.active = true;

    rootStore.menuStore.clearStore();
  }
}
